"use client";

import { useMemo, useState } from "react";
import {
  ErrorNotice,
  SelectField,
  TextAreaField,
  TextField,
} from "@/components/tool-kit/primitives";
import { WIDGET_H2 } from "@/components/tool-kit/styles";
import {
  EXPRESSION_ERROR_STRINGS,
  expressionErrorMessage,
} from "@/components/tools/calculus/expression-errors";
import { RateResults } from "@/components/tools/calculus/rate-results";
import {
  type Constant,
  readConstant,
} from "@/components/tools/calculus/read-constant";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import {
  type Node,
  substitute,
  variable as variableNode,
} from "@/lib/tool-kit/calculus/ast";
import {
  parseXYPairs,
  type XYPoint,
} from "@/lib/tool-kit/calculus/data-points";
import { differentiate } from "@/lib/tool-kit/calculus/differentiate";
import { compile } from "@/lib/tool-kit/calculus/evaluate";
import { parseExpression } from "@/lib/tool-kit/calculus/parse";
import { pointAt } from "@/lib/tool-kit/calculus/rate";

/** English strings. A translated content file overrides these through `ui`. */
export const AVERAGE_RATE_OF_CHANGE_CALCULATOR_STRINGS = {
  modeLabel: "Your data is",
  modes: { function: "A function f(x)", table: "A table of values" },
  functionLabel: "Function f(x)",
  functionHint:
    "Use + − * / ^ and brackets. Functions: sin, cos, tan, exp, ln, log (base 10), sqrt, abs and more. Constants: pi and e.",
  aLabel: "Start x = a",
  bLabel: "End x = b",
  pointHint: "A number or constant such as 1, −2.5 or pi/2.",
  tableLabel: "Table of values, one “x, y” pair per line",
  tableHint:
    "For example time and distance. The x-values need not be in order or evenly spaced.",
  tablePlaceholder: "0, 0\n1, 4.9\n2, 19.6",
  yUnitLabel: "Unit of y (optional)",
  xUnitLabel: "Unit of x (optional)",
  yUnitPlaceholder: "metres",
  xUnitPlaceholder: "second",
  results: "Results",
  rateLabel: "Average rate of change",
  deltaYLabel: "Change in y, Δy",
  deltaXLabel: "Change in x, Δx",
  startValueLabel: "y at the start",
  endValueLabel: "y at the end",
  exactLabel: "Exact form",
  interpretationHeading: "What it means",
  interpretationUnits:
    "From x = {a} to x = {b}, y {direction} by {delta} {yUnit} over {span} {xUnit}: on average {rate} {yUnit} per {xUnit}.",
  interpretationPlain:
    "From x = {a} to x = {b}, y {direction} by {delta} while x changes by {span}: on average {rate} per unit of x.",
  increases: "increases",
  decreases: "decreases",
  unchanged: "does not change",
  secantHeading: "Secant line",
  secantNote:
    "The average rate of change is the slope of the secant line through (a, f(a)) and (b, f(b)).",
  approachHeading: "Shrinking the interval",
  approachNote:
    "The average rate over [a, a + h] approaches the instantaneous rate f′({a}) = {derivative} as h → 0.",
  columnH: "h",
  columnInterval: "Interval",
  columnRate: "Average rate",
  mvtHeading: "Mean Value Theorem",
  mvtText:
    "There is a point c between a and b where the instantaneous rate f′(c) equals this average rate: c ≈ {points}.",
  mvtNone:
    "No point where f′ equals the average rate was found between a and b. The Mean Value Theorem needs f to be continuous on [a, b] and differentiable between.",
  tableRatesHeading: "Rate between consecutive points",
  columnFrom: "From x",
  columnTo: "To x",
  columnDy: "Δy",
  columnDx: "Δx",
  graphHeading: "Graph",
  graphTitleFunction:
    "Graph of f with the secant line between x = {a} and x = {b}",
  graphTitleTable:
    "Plot of the data with the secant line between x = {a} and x = {b}",
  seriesFunction: "y = f(x)",
  seriesData: "Data",
  seriesSecant: "Secant line (slope {rate})",
  seriesTangent: "Tangent parallel to the secant at c",
  pointLabelText: "({x}, {y})",
  downloadPng: "Download graph (PNG)",
  downloadSvg: "Download graph (SVG)",
  undefinedAt:
    "f has no real value at x = {at}, so the rate cannot be computed there.",
  sameEnds: "a and b must be different.",
  pointInvalid:
    "Enter {field} as a number or constant such as 1, −2.5 or pi/2.",
  oneVariable: "Use a function of one variable, such as x^2.",
  tableNeedsTwo: "Enter at least two “x, y” pairs with different x-values.",
  tableBadLines: "These lines are not a pair of numbers: {lines}.",
  tableMissing:
    "x = {x} is not in the table. Choose x-values that appear in your data.",
  expressionErrors: EXPRESSION_ERROR_STRINGS,
};

export type RateStrings = typeof AVERAGE_RATE_OF_CHANGE_CALCULATOR_STRINGS;
type Mode = "function" | "table";

export type RateView =
  | { kind: "problem"; message: string }
  | {
      kind: "function";
      ast: Node;
      derivative: Node;
      f: (x: number) => number;
      df: (x: number) => number;
      a: Constant;
      b: Constant;
      decimals: boolean;
    }
  | {
      kind: "table";
      points: XYPoint[];
      a: number;
      b: number;
      ya: number;
      yb: number;
    };

interface Inputs {
  mode: Mode;
  text: string;
  aText: string;
  bText: string;
  tableText: string;
}

function pair(
  inputs: Inputs,
  strings: RateStrings
): { a: Constant; b: Constant } | { error: string } {
  const a = readConstant(inputs.aText);
  const b = readConstant(inputs.bText);
  if (a === null || a === "invalid") {
    return {
      error: t(strings.pointInvalid, { field: strings.aLabel }),
    };
  }
  if (b === null || b === "invalid") {
    return {
      error: t(strings.pointInvalid, { field: strings.bLabel }),
    };
  }
  if (a.value === b.value) {
    return { error: strings.sameEnds };
  }
  return { a, b };
}

function functionView(inputs: Inputs, strings: RateStrings): RateView {
  const parsed = parseExpression(inputs.text);
  if (!parsed.ok) {
    return {
      kind: "problem",
      message: expressionErrorMessage(parsed.error, strings.expressionErrors),
    };
  }
  if (parsed.variables.length > 1) {
    return { kind: "problem", message: strings.oneVariable };
  }
  const ends = pair(inputs, strings);
  if ("error" in ends) {
    return { kind: "problem", message: ends.error };
  }
  const original = parsed.variables[0] ?? "x";
  const ast =
    original === "x"
      ? parsed.ast
      : substitute(parsed.ast, original, variableNode("x"));
  const derivative = differentiate(ast, "x").derivative;
  return {
    kind: "function",
    ast,
    derivative,
    f: compile(ast, "x"),
    df: compile(derivative, "x"),
    a: ends.a,
    b: ends.b,
    decimals: parsed.usesDecimals,
  };
}

function tableView(inputs: Inputs, strings: RateStrings): RateView {
  const { points, badLines } = parseXYPairs(inputs.tableText);
  if (badLines.length > 0) {
    return {
      kind: "problem",
      message: t(strings.tableBadLines, { lines: badLines.join(", ") }),
    };
  }
  const distinct = new Set(points.map((p) => p.x));
  if (distinct.size < 2) {
    return { kind: "problem", message: strings.tableNeedsTwo };
  }
  const sorted = [...points].sort((p, q) => p.x - q.x);
  const fallbackA = sorted[0]?.x ?? Number.NaN;
  const fallbackB = sorted.at(-1)?.x ?? Number.NaN;
  const aRead = inputs.aText.trim() === "" ? null : readConstant(inputs.aText);
  const bRead = inputs.bText.trim() === "" ? null : readConstant(inputs.bText);
  const a = aRead && aRead !== "invalid" ? aRead.value : fallbackA;
  const b = bRead && bRead !== "invalid" ? bRead.value : fallbackB;
  const start = pointAt(points, a);
  const end = pointAt(points, b);
  if (start === null || end === null) {
    return {
      kind: "problem",
      message: t(strings.tableMissing, { x: start === null ? a : b }),
    };
  }
  if (start.x === end.x) {
    return { kind: "problem", message: strings.sameEnds };
  }
  return {
    kind: "table",
    points: sorted,
    a: start.x,
    b: end.x,
    ya: start.y,
    yb: end.y,
  };
}

export default function AverageRateOfChangeCalculatorWidget() {
  const strings = useToolUi(AVERAGE_RATE_OF_CHANGE_CALCULATOR_STRINGS);
  const fmt = useToolNumber();
  const [mode, setMode] = useState<Mode>("function");
  const [text, setText] = useState("x^2");
  const [aText, setAText] = useState("1");
  const [bText, setBText] = useState("3");
  const [tableText, setTableText] = useState(
    "0, 0\n1, 4.9\n2, 19.6\n3, 44.1\n4, 78.4"
  );
  const [tableA, setTableA] = useState("1");
  const [tableB, setTableB] = useState("3");
  const [yUnit, setYUnit] = useState("");
  const [xUnit, setXUnit] = useState("");
  const startText = mode === "function" ? aText : tableA;
  const endText = mode === "function" ? bText : tableB;
  const view = useMemo(() => {
    const inputs = { mode, text, aText: startText, bText: endText, tableText };
    return mode === "function"
      ? functionView(inputs, strings)
      : tableView(inputs, strings);
  }, [mode, text, startText, endText, tableText, strings]);
  const setStart = mode === "function" ? setAText : setTableA;
  const setEnd = mode === "function" ? setBText : setTableB;
  return (
    <>
      <div className="grid gap-4 sm:grid-cols-2">
        <SelectField
          id="ar-mode"
          label={strings.modeLabel}
          onChange={setMode}
          options={(["function", "table"] as const).map((value) => ({
            value,
            label: strings.modes[value],
          }))}
          value={mode}
        />
        {mode === "function" ? (
          <TextField
            hint={strings.functionHint}
            id="ar-function"
            label={strings.functionLabel}
            mono
            onChange={setText}
            value={text}
          />
        ) : (
          <div className="sm:col-span-2">
            <TextAreaField
              hint={strings.tableHint}
              id="ar-table"
              label={strings.tableLabel}
              onChange={setTableText}
              placeholder={strings.tablePlaceholder}
              rows={6}
              value={tableText}
            />
          </div>
        )}
        <TextField
          id="ar-a"
          label={strings.aLabel}
          mono
          onChange={setStart}
          value={startText}
        />
        <TextField
          hint={strings.pointHint}
          id="ar-b"
          label={strings.bLabel}
          mono
          onChange={setEnd}
          value={endText}
        />
        <TextField
          id="ar-yunit"
          label={strings.yUnitLabel}
          onChange={setYUnit}
          placeholder={strings.yUnitPlaceholder}
          value={yUnit}
        />
        <TextField
          id="ar-xunit"
          label={strings.xUnitLabel}
          onChange={setXUnit}
          placeholder={strings.xUnitPlaceholder}
          value={xUnit}
        />
      </div>
      <section aria-live="polite" className="space-y-5">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {view.kind === "problem" ? (
          <ErrorNotice>{view.message}</ErrorNotice>
        ) : null}
        {view.kind === "problem" ? null : (
          <RateResults
            fmt={fmt}
            strings={strings}
            units={{ x: xUnit.trim(), y: yUnit.trim() }}
            view={view}
          />
        )}
      </section>
    </>
  );
}
